import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import Card3D from '../../../components/base/Card3D';
import MagneticButton from '../../../components/base/MagneticButton';
import ParticleBackground from '../../../components/base/ParticleBackground';

export default function RMindSection() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();

  return (
    <section
      id="rmind"
      className={`py-16 md:py-20 lg:py-24 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#161b22]' : 'bg-[#f8fafb]'
      }`}
    >
      {/* 背景装饰 */}
      <div
        className={`absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/10' : 'from-[#E84A1F]/5'
        } via-transparent to-transparent blur-3xl`}
      ></div>
      <div
        className={`absolute bottom-0 left-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-radial ${
          isDark ? 'from-[#F06030]/5' : 'from-[#F06030]/3'
        } via-transparent to-transparent blur-3xl`}
      ></div>

      {/* AI 风格中心光晕 */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[1000px] h-[600px] md:h-[1000px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/[0.05]' : 'from-[#E84A1F]/[0.03]'
        } via-[#F06030]/[0.01] to-transparent blur-[100px]`}
      ></div>

      {/* 顶部渐变 */}
      <div
        className={`absolute top-0 left-0 right-0 h-[200px] md:h-[350px] bg-gradient-to-b ${
          isDark ? 'from-[#0d1117]/60' : 'from-white/60'
        } to-transparent`}
      ></div>

      {/* 网格背景 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'}`}
        style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'} 1px, transparent 1px), linear-gradient(90deg, ${
            isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'
          } 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* AI 神经网络风格点阵 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
        style={{
          backgroundImage: 'radial-gradient(circle, #E84A1F 2px, transparent 2px)',
          backgroundSize: '60px 60px',
        }}
      ></div>

      {/* Neural Network Lines */}
      <div className={`absolute inset-0 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'} hidden md:block`}>
        <div
          className={`absolute top-[20%] left-[10%] w-[300px] h-[300px] border ${
            isDark ? 'border-[#E84A1F]/30' : 'border-[#E84A1F]/20'
          } rounded-full`}
        ></div>
        <div
          className={`absolute bottom-[15%] right-[15%] w-[200px] h-[200px] border ${
            isDark ? 'border-[#F06030]/30' : 'border-[#F06030]/20'
          } rounded-full`}
        ></div>
      </div>

      {/* AI 脑波风格装饰线 */}
      <svg
        className="absolute top-[10%] left-0 w-full h-[60px] md:h-[100px] opacity-[0.03] hidden md:block"
        viewBox="0 0 1440 100"
        preserveAspectRatio="none"
      >
        <path d="M0,50 Q180,20 360,50 T720,50 T1080,50 T1440,50" fill="none" stroke="#E84A1F" strokeWidth="1" />
        <path d="M0,60 Q180,30 360,60 T720,60 T1080,60 T1440,60" fill="none" stroke="#F06030" strokeWidth="0.5" />
      </svg>

      {/* 装饰性圆环 */}
      <div
        className={`absolute top-[5%] left-[8%] w-[100px] md:w-[180px] h-[100px] md:h-[180px] border ${
          isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
        } rounded-full hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[8%] right-[5%] w-[120px] md:w-[200px] h-[120px] md:h-[200px] border border-dashed ${
          isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'
        } rounded-full hidden md:block`}
      ></div>

      {/* 浮动 AI 节点 */}
      <div
        className={`absolute top-[18%] right-[12%] w-3 h-3 ${
          isDark ? 'bg-[#E84A1F]/25' : 'bg-[#E84A1F]/15'
        } rounded-full animate-pulse hidden lg:block`}
      ></div>
      <div
        className={`absolute top-[35%] left-[6%] w-2 h-2 ${
          isDark ? 'bg-[#F06030]/30' : 'bg-[#F06030]/20'
        } rounded-full animate-pulse hidden lg:block`}
        style={{ animationDelay: '0.5s' }}
      ></div>
      <div
        className={`absolute bottom-[22%] right-[20%] w-4 h-4 border ${
          isDark ? 'border-[#E84A1F]/25' : 'border-[#E84A1F]/15'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute bottom-[40%] left-[12%] w-2 h-2 ${
          isDark ? 'bg-[#E84A1F]/20' : 'bg-[#E84A1F]/10'
        } rounded-full animate-pulse hidden lg:block`}
        style={{ animationDelay: '1s' }}
      ></div>

      {/* 粒子背景效果 */}
      <ParticleBackground 
        particleCount={75} 
        speed={0.45} 
        connectDistance={105}
      />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 mb-8 md:mb-12 relative transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 左侧编号栏 */}
          <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
            <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
              <span className="text-white font-bold text-base md:text-lg">06</span>
            </div>
            <div
              className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent transition-all duration-1000 delay-300 ${
                headerVisible ? 'h-16' : 'h-0'
              }`}
            ></div>
          </div>

          {/* 标题内容 */}
          <div className="flex-1 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-0">
            <div className="relative">
              <div className="inline-flex items-center gap-3 mb-3 md:mb-4">
                <div className="w-2 h-2 bg-[#E84A1F] rounded-full animate-pulse"></div>
                <span className="text-xs text-[#E84A1F] tracking-[0.2em] md:tracking-[0.3em] uppercase">AI-Powered</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E84A1F] via-[#F06030] to-[#E84A1F]">
                  {t('rmind.title')}
                </span>
              </h2>
              <p
                className={`text-base md:text-lg font-light mt-1 md:mt-2 ${
                  isDark ? 'text-gray-400' : 'text-[#0d1117]/60'
                }`}
              >
                {t('rmind.subtitle')}
              </p>
            </div>

            <div
              className={`max-w-full md:max-w-[380px] text-sm leading-relaxed pb-0 md:pb-2 border-l-0 md:border-l border-[#E84A1F]/30 pl-0 md:pl-6 transition-all duration-1000 delay-500 ${
                isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
              } ${headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
            >
              {t('rmind.desc1')}
            </div>
          </div>
        </div>

        {/* 主内容区 */}
        <div ref={mainRef} className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* 左侧大卡片 - AI 能力概览 */}
          <div
            className={`lg:col-span-4 relative group transition-all duration-1000 ${
              mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:-translate-x-10'
            }`}
          >
            <Card3D className="h-full" intensity={10} scale={1.02}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-xl md:rounded-2xl p-4 md:p-6 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden flex flex-col shadow-lg ${
                  isDark
                    ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}
              >
                {/* 装饰性圆环 */}
                <div
                  className={`absolute -top-16 -right-16 w-48 h-48 border ${
                    isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                  } rounded-full hidden md:block`}
                ></div>
                <div
                  className={`absolute -top-8 -right-8 w-32 h-32 border ${
                    isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
                  } rounded-full hidden md:block`}
                ></div>

                {/* 顶部标签 */}
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="inline-flex items-center gap-2 px-2 md:px-2.5 py-1 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-[#E84A1F] rounded-full animate-pulse"></div>
                    <span className="text-[10px] text-[#E84A1F] uppercase tracking-wider">Intelligence</span>
                  </div>
                  <div className={`text-xs ${isDark ? 'text-white/20' : 'text-[#0d1117]/20'}`}>01</div>
                </div>

                {/* 图标和标题区域 */}
                <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 flex items-center justify-center hidden md:flex">
                      <div
                        className={`w-16 h-16 border ${
                          isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                        } rounded-full animate-[spin_20s_linear_infinite]`}
                      ></div>
                    </div>
                    <div className="relative w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-lg md:rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(232,74,31,0.4)] group-hover:shadow-[0_0_50px_rgba(232,74,31,0.5)] transition-all">
                      <i className="ri-brain-line text-lg md:text-xl text-white"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-base md:text-lg font-bold mb-0.5 md:mb-1 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('rmind.ai.title')}
                    </h3>
                    <p
                      className={`text-[10px] md:text-xs leading-relaxed ${
                        isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                      }`}
                    >
                      {t('rmind.desc2')}
                    </p>
                  </div>
                </div>

                {/* 技术标签 */}
                <div className="flex flex-wrap gap-1.5 mb-3 md:mb-4">
                  <span className="px-2 py-0.5 md:py-1 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded-full text-[10px] text-[#E84A1F]">
                    {t('rmind.ai.tag1')}
                  </span>
                  <span className="px-2 py-0.5 md:py-1 bg-[#F06030]/10 border border-[#F06030]/20 rounded-full text-[10px] text-[#F06030]">
                    {t('rmind.ai.tag2')}
                  </span>
                  <span className="px-2 py-0.5 md:py-1 bg-[#C83A15]/10 border border-[#C83A15]/20 rounded-full text-[10px] text-[#C83A15]">
                    {t('rmind.ai.tag3')}
                  </span>
                </div>

                {/* 能力列表 */}
                <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4 flex-1">
                  {[
                    { icon: 'ri-line-chart-line', key: 'rmind.ai.feature1' },
                    { icon: 'ri-shield-flash-line', key: 'rmind.ai.feature2' },
                    { icon: 'ri-flow-chart', key: 'rmind.ai.feature3' },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 md:gap-2.5 p-2 md:p-2.5 border rounded-lg group/item hover:border-[#E84A1F]/20 transition-all duration-500 ${
                        isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                      } ${mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      style={{ transitionDelay: `${300 + index * 100}ms` }}
                    >
                      <div className="w-6 md:w-7 h-6 md:h-7 bg-[#E84A1F]/10 rounded-lg flex items-center justify-center">
                        <i className={`${item.icon} text-[#E84A1F] text-xs md:text-sm`}></i>
                      </div>
                      <span className={`text-[10px] md:text-xs ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>
                        {t(item.key)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <MagneticButton
                  intensity={0.35}
                  scale={1.05}
                  className="group/btn relative w-full px-3 md:px-4 py-2.5 md:py-3 bg-gradient-to-r from-[#E84A1F] to-[#F06030] rounded-lg md:rounded-xl text-xs md:text-sm font-semibold text-white overflow-hidden transition-shadow hover:shadow-[0_0_30px_rgba(232,74,31,0.4)] whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {t('rmind.cta')}
                    <div className="w-4 md:w-5 h-4 md:h-5 bg-white/20 rounded-full flex items-center justify-center group-hover/btn:bg-white/30 transition-colors">
                      <i className="ri-arrow-right-up-line text-[10px] md:text-xs"></i>
                    </div>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F06030] to-[#E84A1F] opacity-0 group-hover/btn:opacity-100 transition-opacity"></div>
                </MagneticButton>
              </div>
            </Card3D>
          </div>

          {/* 右侧内容区 */}
          <div className="lg:col-span-8 flex flex-col gap-3 md:gap-4">
            {/* 三个功能卡片 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {/* Card 1 - 智能分析 */}
              <div
                className={`relative group transition-all duration-700 ${
                  mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: '100ms' }}
              >
                <Card3D intensity={12} scale={1.04}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div
                    className={`relative border rounded-xl md:rounded-2xl p-3 md:p-4 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                      isDark
                        ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                        : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                    }`}
                  >
                    <div className={`absolute top-2 md:top-3 right-2 md:right-3 text-xs font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>01</div>
                    <div className="w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-3 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-[#E84A1F]/20">
                      <i className="ri-brain-line text-base md:text-lg text-white"></i>
                    </div>
                    <h3
                      className={`text-xs md:text-sm font-semibold mb-1 md:mb-1.5 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('rmind.analysis.title')}
                    </h3>
                    <p className={`text-[10px] md:text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                      {t('rmind.analysis.desc')}
                    </p>
                  </div>
                </Card3D>
              </div>

              {/* Card 2 - 自动化工作流 */}
              <div
                className={`relative group transition-all duration-700 ${
                  mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: '200ms' }}
              >
                <Card3D intensity={12} scale={1.04}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div
                    className={`relative border rounded-xl md:rounded-2xl p-3 md:p-4 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                      isDark
                        ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                        : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                    }`}
                  >
                    <div className={`absolute top-2 md:top-3 right-2 md:right-3 text-xs font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>02</div>
                    <div className="w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#F06030] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-3 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-[#F06030]/20">
                      <i className="ri-robot-line text-base md:text-lg text-white"></i>
                    </div>
                    <h3
                      className={`text-xs md:text-sm font-semibold mb-1 md:mb-1.5 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('rmind.automation.title')}
                    </h3>
                    <p className={`text-[10px] md:text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                      {t('rmind.automation.desc')}
                    </p>
                  </div>
                </Card3D>
              </div>

              {/* Card 3 - RWA 优化 */}
              <div
                className={`relative group transition-all duration-700 ${
                  mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: '300ms' }}
              >
                <Card3D intensity={12} scale={1.04}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C83A15]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div
                    className={`relative border rounded-xl md:rounded-2xl p-3 md:p-4 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                      isDark
                        ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                        : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                    }`}
                  >
                    <div className={`absolute top-2 md:top-3 right-2 md:right-3 text-xs font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>03</div>
                    <div className="w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#C83A15] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-3 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-[#C83A15]/20">
                      <i className="ri-shield-check-line text-base md:text-lg text-white"></i>
                    </div>
                    <h3
                      className={`text-xs md:text-sm font-semibold mb-1 md:mb-1.5 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('rmind.rwa.title')}
                    </h3>
                    <p className={`text-[10px] md:text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                      {t('rmind.rwa.desc')}
                    </p>
                  </div>
                </Card3D>
              </div>
            </div>

            {/* 核心原则卡片 */}
            <div
              className={`relative group transition-all duration-700 ${
                mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/5 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-xl md:rounded-2xl p-4 md:p-5 hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                  isDark
                    ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}
              >
                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#E84A1F]/20 to-[#E84A1F]/5 rounded-lg flex items-center justify-center">
                    <i className="ri-lightbulb-line text-sm md:text-base text-[#E84A1F]"></i>
                  </div>
                  <h3 className={`text-xs md:text-sm font-semibold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                    {t('rmind.principles.title')}
                  </h3>
                  <div className={`flex-1 h-px bg-gradient-to-r from-[#E84A1F]/20 to-transparent hidden sm:block`}></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
                  {/* Principle 1 */}
                  <div
                    className={`flex items-center gap-2 md:gap-2.5 border rounded-lg md:rounded-xl p-2.5 md:p-3 hover:border-[#E84A1F]/20 transition-all group/item ${
                      isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                    }`}
                  >
                    <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#E84A1F]/20 to-[#E84A1F]/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform">
                      <i className="ri-checkbox-circle-line text-base md:text-lg text-[#E84A1F]"></i>
                    </div>
                    <p className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>{t('rmind.principles.rule')}</p>
                  </div>

                  {/* Principle 2 */}
                  <div
                    className={`flex items-center gap-2 md:gap-2.5 border rounded-lg md:rounded-xl p-2.5 md:p-3 hover:border-[#E84A1F]/20 transition-all group/item ${
                      isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                    }`}
                  >
                    <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#F06030]/20 to-[#F06030]/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform">
                      <i className="ri-shield-line text-base md:text-lg text-[#F06030]"></i>
                    </div>
                    <p className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>{t('rmind.principles.verify')}</p>
                  </div>

                  {/* Principle 3 */}
                  <div
                    className={`flex items-center gap-2 md:gap-2.5 border rounded-lg md:rounded-xl p-2.5 md:p-3 hover:border-[#E84A1F]/20 transition-all group/item ${
                      isDark ? 'bg-[#161b22] border-white/10' : 'bg-[#f8fafb] border-[#0d1117]/10'
                    }`}
                  >
                    <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#C83A15]/20 to-[#C83A15]/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform">
                      <i className="ri-file-list-3-line text-base md:text-lg text-[#C83A15]"></i>
                    </div>
                    <p className={`text-[10px] md:text-xs font-medium ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>{t('rmind.principles.audit')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 底部两个信息卡片 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {/* Security Card */}
              <div
                className={`relative group transition-all duration-700 ${
                  mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 sm:-translate-x-10'
                }`}
                style={{ transitionDelay: '500ms' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-3 md:p-4 hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                    isDark
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}
                >
                  <div className={`absolute -top-8 -right-8 w-20 md:w-24 h-20 md:h-24 ${isDark ? 'bg-[#E84A1F]/5' : 'bg-[#E84A1F]/3'} rounded-full blur-[40px]`}></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 md:gap-2.5 mb-2 md:mb-3">
                      <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-lg flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
                        <i className="ri-lock-line text-sm md:text-base text-white"></i>
                      </div>
                      <h4 className={`text-xs md:text-sm font-semibold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                        {t('rmind.security.title')}
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      <span
                        className={`flex items-center gap-1 md:gap-1.5 px-2 py-0.5 md:py-1 border rounded-lg text-[10px] ${
                          isDark
                            ? 'bg-[#161b22] border-white/10 text-gray-400'
                            : 'bg-[#f8fafb] border-[#0d1117]/10 text-[#0d1117]/60'
                        }`}
                      >
                        <div className="w-1 h-1 bg-[#E84A1F] rounded-full"></div>
                        {t('rmind.security.item1')}
                      </span>
                      <span
                        className={`flex items-center gap-1 md:gap-1.5 px-2 py-0.5 md:py-1 border rounded-lg text-[10px] ${
                          isDark
                            ? 'bg-[#161b22] border-white/10 text-gray-400'
                            : 'bg-[#f8fafb] border-[#0d1117]/10 text-[#0d1117]/60'
                        }`}
                      >
                        <div className="w-1 h-1 bg-[#F06030] rounded-full"></div>
                        {t('rmind.security.item2')}
                      </span>
                      <span
                        className={`flex items-center gap-1 md:gap-1.5 px-2 py-0.5 md:py-1 border rounded-lg text-[10px] ${
                          isDark
                            ? 'bg-[#161b22] border-white/10 text-gray-400'
                            : 'bg-[#f8fafb] border-[#0d1117]/10 text-[#0d1117]/60'
                        }`}
                      >
                        <div className="w-1 h-1 bg-[#C83A15] rounded-full"></div>
                        {t('rmind.security.item3')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Future Card */}
              <div
                className={`relative group transition-all duration-700 ${
                  mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 sm:translate-x-10'
                }`}
                style={{ transitionDelay: '600ms' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-3 md:p-4 hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                    isDark
                      ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                      : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}
                >
                  <div className={`absolute -top-8 -right-8 w-20 md:w-24 h-20 md:h-24 ${isDark ? 'bg-[#F06030]/5' : 'bg-[#F06030]/3'} rounded-full blur-[40px]`}></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 md:gap-2.5 mb-2 md:mb-3">
                      <div className="w-7 md:w-8 h-7 md:h-8 bg-gradient-to-br from-[#F06030] to-[#E84A1F] rounded-lg flex items-center justify-center shadow-lg shadow-[#F06030]/20">
                        <i className="ri-rocket-line text-sm md:text-base text-white"></i>
                      </div>
                      <h4 className={`text-xs md:text-sm font-semibold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>
                        {t('rmind.future.title')}
                      </h4>
                    </div>
                    <p className={`text-[10px] md:text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>
                      {t('rmind.future.desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部统计指标 */}
        <div ref={statsRef} className="mt-4 md:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            { value: '50+', labelKey: 'rmind.stats.models', icon: 'ri-cpu-line' },
            { value: '1M+', labelKey: 'rmind.stats.data', icon: 'ri-database-2-line' },
            { value: '<100ms', labelKey: 'rmind.stats.latency', icon: 'ri-timer-line' },
            { value: '99.99%', labelKey: 'rmind.stats.accuracy', icon: 'ri-shield-star-line' },
          ].map((stat, index) => (
            <Card3D key={index} intensity={15} scale={1.04}>
              <div
                className={`border rounded-lg md:rounded-xl p-2.5 md:p-3 text-center relative group hover:border-[#E84A1F]/20 transition-all duration-500 cursor-pointer shadow-lg ${
                  isDark
                    ? 'bg-[#0d1117] border-white/10 shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-[#0d1117]/5'
                } ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-[#E84A1F]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg md:rounded-xl"></div>

                <div className="relative z-10 flex items-center gap-2 md:gap-3">
                  <div className="w-8 md:w-9 h-8 md:h-9 bg-gradient-to-br from-[#E84A1F]/10 to-[#F06030]/10 border border-[#E84A1F]/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className={`${stat.icon} text-sm md:text-base text-[#E84A1F]`}></i>
                  </div>
                  <div className="text-left">
                    <div className={`text-base md:text-lg font-bold group-hover:text-[#E84A1F] transition-colors ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>{stat.value}</div>
                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t(stat.labelKey)}</div>
                  </div>
                </div>

                <div className={`absolute top-1.5 md:top-2 right-1.5 md:right-2 text-[10px] font-mono ${isDark ? 'text-white/10' : 'text-[#0d1117]/10'}`}>0{index + 1}</div>
              </div>
            </Card3D>
          ))}
        </div>

        {/* 底部连接线装饰 */}
        <div className="hidden md:flex items-center justify-center mt-6 gap-2">
          <div className={`w-20 h-px bg-gradient-to-r from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/50"></div>
          <div className="w-2 h-2 rounded-full bg-[#E84A1F]/30"></div>
          <div className={`w-20 h-px bg-gradient-to-l from-transparent ${isDark ? 'to-white/10' : 'to-[#0d1117]/10'}`}></div>
        </div>
      </div>
    </section>
  );
}
